/**
 * @fileoverview Легкий pub/sub для состояния и событий.
 * Без React — подписка из игрового цикла, canvas или UI-склейки.
 */

/** Слушатель без аргументов для {@link GameStore.subscribe}. */
export type Listener = () => void;

/**
 * Минимальный store: хранит значение, shallow-merge или замена, уведомляет подписчиков.
 *
 * @typeParam T — форма состояния (для merge удобен обычный объект)
 */
export class GameStore<T> {
  private state: T;
  private readonly listeners = new Set<Listener>();

  /** @param state — начальное значение */
  constructor(state: T) {
    this.state = state;
  }

  /** Текущий снимок (тот же reference до следующего обновления). */
  getState(): T {
    return this.state;
  }

  /**
   * Обновить состояние и уведомить слушателей.
   * - Частичные объекты: shallow-merge `{ ...state, ...partial }`
   * - Функции-апдейтеры получают текущее состояние и должны вернуть следующее
   *
   * Для полной замены верхнего уровня предпочтителен {@link GameStore.replace}.
   */
  setState(partial: Partial<T> | ((s: T) => T)): void {
    const next =
      typeof partial === "function"
        ? partial(this.state)
        : ({ ...this.state, ...partial } as T);
    this.replace(next);
  }

  /**
   * Полностью заменить состояние и уведомить слушателей.
   * No-op (без notify), если `next` ссылочно равен текущему.
   */
  replace(next: T): void {
    if (Object.is(next, this.state)) return;
    this.state = next;
    for (const fn of this.listeners) fn();
  }

  /**
   * Подписаться на любое изменение состояния.
   * @returns функция отписки
   */
  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }
}

/** Слушатель полезной нагрузки для {@link createEmitter}. */
export type EmitterListener<T> = (payload: T) => void;

/** Типизированная шина событий из {@link createEmitter}. */
export interface Emitter<T> {
  /** Подписка; возвращает отписку. */
  on(fn: EmitterListener<T>): () => void;
  /** Уведомить всех слушателей с `payload`. */
  emit(payload: T): void;
  /** Сбросить всех слушателей. */
  clear(): void;
}

/**
 * Создать типизированную шину (тосты, вспышка экрана, one-shot FX и т.п.).
 *
 * @example
 * const toast = createEmitter<string>();
 * const off = toast.on((msg) => showToast(msg));
 * toast.emit("Полоса очищена");
 * off();
 */
export function createEmitter<T = void>(): Emitter<T> {
  const listeners = new Set<EmitterListener<T>>();
  return {
    on(fn) {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
    emit(payload) {
      for (const fn of listeners) fn(payload);
    },
    clear() {
      listeners.clear();
    },
  };
}
